/**
 * F41 (Wave 2 Track D — admin) — platform-wide tournament analytics.
 *
 * Route: `/tournaments/analytics`
 * Permission: `tournaments:read` (super_admin + support + content_moderator).
 *
 * Surface: a KPI Stat row (total registrations / confirmed / suspended / total
 * listings), a Bar chart of tournaments by sport, a Donut chart of tournaments
 * by city, a Bar chart of listing-fee revenue by month, and a Line chart of
 * registration volume over time. Mirrors F40's promotions analytics page.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Chart, EmptyState, Skeleton, Stat } from '@bravemove/ui-web';
import { adminTournamentsApi, type AdminTournamentAnalyticsResponse } from '../../api/tournaments';
import { translateApiError } from '../../auth/errors';
import { PageHeader } from '../../components/PageHeader';

function humaniseSport(sport: string): string {
  return sport.replace(/-/g, ' ');
}

export function AdminTournamentAnalyticsPage() {
  const { t, locale } = useI18n();

  const [data, setData] = useState<AdminTournamentAnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await adminTournamentsApi.getAnalytics());
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchData();
  }, [fetchData]);

  const bySportData = useMemo(
    () => (data?.bySport ?? []).map((r) => ({ sport: humaniseSport(r.sportName), count: r.count })),
    [data],
  );

  const byCityData = useMemo(
    () =>
      (data?.byCity ?? []).map((r) => ({
        city: r.cityCode ?? t('admin:tournament.analytics.byCity', { defaultValue: 'Unknown' }),
        count: r.count,
      })),
    [data, t],
  );

  const feeRevenueData = useMemo(
    () => (data?.feeRevenueByMonth ?? []).map((r) => ({ month: r.month, fee: r.feeMinor / 100 })),
    [data],
  );

  const registrationsData = useMemo(
    () => (data?.registrationsByDay ?? []).map((r) => ({ day: r.day, count: r.count })),
    [data],
  );

  const totalListings = useMemo(
    () => (data?.byStatus ?? []).reduce((acc, r) => acc + r.count, 0),
    [data],
  );

  return (
    <div className="space-y-6" data-testid="tournament-analytics-page">
      <PageHeader
        title={t('admin:tournament.analytics.title', { defaultValue: 'Tournament analytics' })}
        subtitle={t('admin:tournament.analytics.subtitle', {
          defaultValue:
            'Platform-wide tournament performance: by sport, by city, fee revenue, and registration volume.',
        })}
      />

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading ? (
        <div className="space-y-3" data-testid="tournament-analytics-loading">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-24 w-full rounded-lg" />
          ))}
        </div>
      ) : !data ? (
        <EmptyState
          title={t('admin:tournament.analytics.empty', {
            defaultValue: 'No tournament analytics yet.',
          })}
        />
      ) : (
        <>
          <section
            className="grid grid-cols-2 gap-3 md:grid-cols-4"
            data-testid="tournament-analytics-kpis"
          >
            <Stat
              label={t('admin:tournament.analytics.totalRegistrations', {
                defaultValue: 'Total registrations',
              })}
              value={data.totalRegistrations.toLocaleString(locale)}
            />
            <Stat
              label={t('admin:tournament.analytics.confirmedRegistrations', {
                defaultValue: 'Confirmed registrations',
              })}
              value={data.confirmedRegistrations.toLocaleString(locale)}
            />
            <Stat
              label={t('admin:tournament.analytics.suspended', { defaultValue: 'Suspended' })}
              value={data.suspendedCount.toLocaleString(locale)}
            />
            <Stat
              label={t('admin:tournament.analytics.totalListings', {
                defaultValue: 'Total listings',
              })}
              value={totalListings.toLocaleString(locale)}
            />
          </section>

          {/* Tournaments by sport (bar) */}
          <section className="space-y-2" data-testid="tournament-analytics-by-sport">
            <Chart
              kind="bar"
              data={bySportData}
              xKey="sport"
              series={[
                {
                  key: 'count',
                  label: t('admin:tournament.analytics.bySport', {
                    defaultValue: 'Tournaments by sport',
                  }),
                },
              ]}
              title={t('admin:tournament.analytics.bySport', {
                defaultValue: 'Tournaments by sport',
              })}
              height={240}
            />
          </section>

          {/* Tournaments by city (donut) */}
          <section className="space-y-2" data-testid="tournament-analytics-by-city">
            <Chart
              kind="donut"
              data={byCityData}
              xKey="city"
              series={[
                {
                  key: 'count',
                  label: t('admin:tournament.analytics.byCity', {
                    defaultValue: 'Tournaments by city',
                  }),
                },
              ]}
              title={t('admin:tournament.analytics.byCity', {
                defaultValue: 'Tournaments by city',
              })}
              height={260}
            />
          </section>

          {/* Listing-fee revenue by month (bar) */}
          <section className="space-y-2" data-testid="tournament-analytics-fee-revenue">
            <Chart
              kind="bar"
              data={feeRevenueData}
              xKey="month"
              series={[
                {
                  key: 'fee',
                  label: t('admin:tournament.analytics.feeRevenue', {
                    defaultValue: 'Listing-fee revenue by month',
                  }),
                },
              ]}
              title={t('admin:tournament.analytics.feeRevenue', {
                defaultValue: 'Listing-fee revenue by month',
              })}
              height={240}
            />
          </section>

          {/* Registration volume over time (line) */}
          <section className="space-y-2" data-testid="tournament-analytics-registrations">
            <Chart
              kind="line"
              data={registrationsData}
              xKey="day"
              series={[
                {
                  key: 'count',
                  label: t('admin:tournament.analytics.registrationVolume', {
                    defaultValue: 'Registration volume',
                  }),
                },
              ]}
              title={t('admin:tournament.analytics.registrationVolume', {
                defaultValue: 'Registration volume',
              })}
              height={240}
            />
          </section>
        </>
      )}
    </div>
  );
}

export default AdminTournamentAnalyticsPage;
